/**
 * PaymentModal — iframe YengaPay dans une modale.
 * Portée directement depuis cvh-app, stylée avec la charte UNIQUE (or/blanc).
 */
import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';

interface PaymentModalProps {
  open: boolean;
  checkoutUrl: string | null;
  onSuccess: () => void;
  onClose: () => void;
}

export function PaymentModal({
  open,
  checkoutUrl,
  onSuccess,
  onClose,
}: PaymentModalProps): JSX.Element | null {
  const [status, setStatus] = useState<'pending' | 'checking'>('pending');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const succeededRef = useRef(false);

  useEffect(() => {
    if (!open || !checkoutUrl) return;
    succeededRef.current = false;
    setStatus('pending');
  }, [open, checkoutUrl]);

  const handleIframeLoad = () => {
    if (!open || succeededRef.current) return;
    try {
      const url = iframeRef.current?.contentWindow?.location?.href ?? '';
      if (url) {
        const host = new URL(url).hostname.toLowerCase();
        if (host === 'yengapay.com' || host === 'www.yengapay.com') {
          succeededRef.current = true;
          setStatus('checking');
          window.setTimeout(() => onSuccess(), 800);
        }
      }
    } catch {
      // Cross-origin — normal pendant le checkout
    }
  };

  if (!open || !checkoutUrl) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        className="relative flex flex-col overflow-hidden rounded-2xl"
        style={{
          width: 480,
          height: 620,
          background: 'var(--color-white)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
        }}
      >
        {status === 'pending' && (
          <>
            {/* En-tête */}
            <div
              className="flex items-center justify-between px-5 py-3 shrink-0 border-b"
              style={{ borderColor: 'var(--color-border)' }}
            >
              <div className="flex items-center gap-2">
                <span
                  className="w-2.5 h-2.5 rounded-full animate-pulse"
                  style={{ background: '#22c55e' }}
                />
                <span
                  className="text-sm font-semibold"
                  style={{ color: 'var(--color-text-dark)' }}
                >
                  Paiement sécurisé — YengaPay
                </span>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-lg leading-none transition-unique hover:opacity-60"
                style={{ color: 'var(--color-text-medium)' }}
                aria-label="Fermer"
              >
                ✕
              </button>
            </div>

            {/* Iframe YengaPay */}
            <iframe
              ref={iframeRef}
              src={checkoutUrl}
              className="flex-1 w-full border-0"
              onLoad={handleIframeLoad}
              allow="payment"
              title="Paiement YengaPay"
            />
          </>
        )}

        {status === 'checking' && (
          <div className="flex-1 flex flex-col items-center justify-center gap-5 px-8 text-center">
            <div
              className="w-12 h-12 rounded-full border-4 border-t-transparent animate-spin"
              style={{ borderColor: 'var(--color-primary-glow)', borderTopColor: 'var(--color-primary-dark)' }}
            />
            <div>
              <h3
                className="text-lg font-bold"
                style={{ color: 'var(--color-text-dark)' }}
              >
                Paiement réussi !
              </h3>
              <p
                className="text-sm mt-1"
                style={{ color: 'var(--color-text-medium)' }}
              >
                Activation de votre abonnement…
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
